import { useState } from 'react'
import { Alert, Button, Spin, Tabs, Typography } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { useAdvisoryAiReport } from '@/features/advisory-ai/hooks'
import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import { ReportAdvisoryNotesSection } from './ReportAdvisoryNotesSection'
import { ReportAgendaFindingsSection } from './ReportAgendaFindingsSection'
import { SourceReportsSection } from './SourceReportsSection'
import { ReportSummarySection } from './ReportSummarySection'

const { Text } = Typography

function isTerminalStatus(status?: string | null) {
  const value = String(status || '').toUpperCase()
  return value === AdvisoryCallStatus.COMPLETED_EXPORT || value === AdvisoryCallStatus.ERROR
}

function hasInsightsStatus(status?: string | null) {
  const value = String(status || '').toUpperCase()
  return (
    value === AdvisoryCallStatus.COMPLETED_BRIEF ||
    value === AdvisoryCallStatus.COMPLETED_EXPORT ||
    value === AdvisoryCallStatus.CHAT_IN_PROGRESS ||
    value === AdvisoryCallStatus.CHAT_ERROR
  )
}

type ReportStepProps = {
  callId: string
  clientId: string
  findingsUpdating?: boolean
  onBack: () => void
  onFinish: () => void
  /**
   * Hide the free-text "Meeting Summary" card and instead show the Executive Summary
   * agenda item (with its findings) as the first agenda card. Used by both the
   * consolidated and single-org reviews.
   */
  executiveSummaryAsAgenda?: boolean
}

export function ReportStep({
  callId,
  clientId,
  findingsUpdating = false,
  onBack,
  onFinish,
  executiveSummaryAsAgenda = false,
}: ReportStepProps) {
  const [activeTab, setActiveTab] = useState('analysis')
  const reportQuery = useAdvisoryAiReport(callId, clientId, true)

  const isExecutiveSummary = (title: string) => title.trim().toLowerCase() === 'executive summary'
  const effectiveStatus = (reportQuery.data?.status || AdvisoryCallStatus.PENDING).toUpperCase()
  const statusReady = hasInsightsStatus(effectiveStatus) || isTerminalStatus(effectiveStatus)
  const allAgendaItems = reportQuery.data?.agendaItems ?? []
  const hasExecutiveSummaryAgenda = allAgendaItems.some((item) => isExecutiveSummary(item.title))
  const nonSummaryAgendaItems = allAgendaItems.filter((item) => !isExecutiveSummary(item.title))
  // executiveSummaryAsAgenda: put the Executive Summary agenda item first (no separate summary
  // card). Otherwise it surfaces as the "Meeting Summary" card and is excluded from the agenda list.
  const agendaItemsForFindings = executiveSummaryAsAgenda
    ? [...allAgendaItems.filter((item) => isExecutiveSummary(item.title)), ...nonSummaryAgendaItems]
    : nonSummaryAgendaItems
  const showSummaryCard = !executiveSummaryAsAgenda && hasExecutiveSummaryAgenda
  const hasAnalysisData =
    agendaItemsForFindings.length > 0 || (reportQuery.data?.findings?.length ?? 0) > 0
  const headlinerSummary = (reportQuery.data?.headlinerSummary ?? '').trim()

  const isWaitingForStatus = !statusReady
  const isError = effectiveStatus === AdvisoryCallStatus.ERROR

  if (isWaitingForStatus) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">AI Analysis & Review</h2>
          <p className="text-sm text-gray-600 mb-4">
            Analysis focused on your meeting agenda. Each finding is linked to an agenda item.
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-24 gap-3 min-h-[280px]">
          <Spin size="large" />
          <Text type="secondary">Please wait while we process your report...</Text>
        </div>
      </div>
    )
  }

  const analysisTabContent = (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Analysis focused on your meeting agenda. Each finding is linked to an agenda item.
      </p>

      {headlinerSummary ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Headliner Summary</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{headlinerSummary}</p>
        </div>
      ) : null}

      {showSummaryCard ? (
        <ReportSummarySection callId={callId} clientId={clientId} summary={reportQuery.data?.summary} />
      ) : null}

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="Error generating report"
          description="An error occurred while generating the AI analysis. Please try again."
        />
      ) : null}

      {/* data-tour anchors the assistant walkthroughs — see assistant/mockData.ts */}
      {!isError ? (
        <div data-tour="findings">
          {findingsUpdating ? (
            <div className="flex flex-col items-center justify-center gap-3 py-24 min-h-[400px]">
              <Spin size="large" />
              <Text type="secondary">Updating findings...</Text>
            </div>
          ) : hasAnalysisData ? (
            <ReportAgendaFindingsSection
              callId={callId}
              clientId={clientId}
              agendaItems={agendaItemsForFindings}
              findings={reportQuery.data?.findings || []}
            />
          ) : (
            <Text type="secondary" className="block text-xl text-center py-8">
              No insights available yet.
            </Text>
          )}
        </div>
      ) : null}

      <ReportAdvisoryNotesSection callId={callId} clientId={clientId} notes={reportQuery.data?.notes} />
    </div>
  )

  const rawDataTabContent = (
    <SourceReportsSection
      reportJson={reportQuery.data?.reportJson}
      endDate={reportQuery.data?.endDate}
      revaluationMissing={reportQuery.data?.revaluationMissing}
    />
  )


  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">AI Analysis & Review</h2>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'analysis', label: 'AI Analysis & Review', children: analysisTabContent },
          { key: 'source-reports', label: 'Source Reports', children: rawDataTabContent },
        ]}
      />

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
        <CheckCircleOutlined className="text-green-600 text-lg" />
        <Text className="text-green-900">
          Summary and notes will be used to generate your outputs in the next step.
        </Text>
      </div>

      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <Button onClick={onBack}>← Back</Button>
        <Button
          type="primary"
          onClick={onFinish}
          className="feature-button-solid"
        >
          Next →
        </Button>
      </div>
    </div>
  )
}
